import { Button, useUnmountEffect } from '@timelyapp/react';
import { Tag as TagIcon } from 'components/Icons';
import Modal from 'components/Modal';
import TagsEditorItem from 'components/TagsEditorItem';
import { useWithDispatch } from 'hooks/useWithDispatch';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  clearTagDraft,
  fetchTags,
  initializeTagDraft,
  saveTag,
  sortTag,
  toggleArchiveTag,
  updateTagDraft,
} from 'redux/modules/tags.actions';
import deleteTag from 'redux/modules/tags.actions.delete';
import { getTagsEditor } from 'redux/modules/tags.selectors';
import type { TagDraft } from 'redux/modules/tags.types';
import type { Tag } from 'types/tag';
import styles from './TagsEditor.module.scss';
import TagDeleteConfirmationDialogue from './tags/TagDeleteConfirmationDialogue';

interface Props {
  children?: React.ReactNode;
  modal?: boolean;
}

const TagsEditor: React.FC<Props> = ({ modal }) => {
  const { permissions, tags, ui, xhrStatus } = useSelector(getTagsEditor);
  const [tagActionDetails, setTagActionDetails] = useState<Tag | TagDraft | null>(null);
  const actions = useWithDispatch({
    sortTag,
    saveTag,
    deleteTag,
    toggleArchiveTag,
    initializeTagDraft,
    updateTagDraft,
    clearTagDraft,
    fetchTags,
  });

  useUnmountEffect(() => {
    if (!window.location.pathname.endsWith('/settings/labels')) {
      actions.clearTagDraft();
    }
  });

  const handleInitializeDraft = () => {
    actions.initializeTagDraft();
  };

  const handleDeleteTag = useCallback(async () => {
    if (tagActionDetails?.id) {
      await actions.deleteTag(tagActionDetails?.id);
      setTagActionDetails(null);
    }
  }, [actions, tagActionDetails?.id]);

  function renderTagOption(
    tag: Tag | TagDraft,
    children: Array<React.ReactElement> | undefined
  ): React.ReactElement {
    const sequence = String(tag.sequence) || 'new';
    const key = tag.id ? `${tag.name}-${tag.id}-${sequence}` : 'draft-tag';
    return (
      <TagsEditorItem
        createMode={!tag.id}
        key={key}
        tag={tag}
        onToggleArchive={actions.toggleArchiveTag}
        onSave={actions.saveTag}
        onInitializeTagDraft={actions.initializeTagDraft}
        onCancelDraft={actions.clearTagDraft}
        onSort={actions.sortTag}
        error={ui.error}
        permissions={permissions}
        fetchTags={actions.fetchTags}
        setTagActionDetails={tag => {
          setTagActionDetails(tag);
        }}
      >
        {children}
      </TagsEditorItem>
    );
  }

  const contents = (
    <div>
      {tags.length ? (
        tags.map((tag: Tag | TagDraft) => {
          const assumeTag = tag as Tag;
          return renderTagOption(
            tag,
            assumeTag.children && assumeTag.children.length
              ? assumeTag.children
                  .sort((a, b) => a.sequence - b.sequence)
                  .map(subtag => renderTagOption(subtag, undefined))
              : undefined
          );
        })
      ) : (
        <div className={styles.empty}>
          <TagIcon />
          <h5>{xhrStatus.isLoading ? 'Loading tags...' : 'No tags found'}</h5>
          <p>{!xhrStatus.isLoading ? 'Create a tag to get started!' : null}</p>
        </div>
      )}
      <TagDeleteConfirmationDialogue
        tag={tagActionDetails}
        setTagActionDetails={setTagActionDetails}
        handleDeleteTag={handleDeleteTag}
        handleArchiveTag={actions.toggleArchiveTag}
      />
    </div>
  );

  if (modal) {
    return (
      <Modal
        isOpen
        animated={false}
        origin="center"
        defaultWidth={640}
        defaultHeight={700}
        position="fixed"
        backdrop="light"
        showCloseIcon
      >
        <div className={styles.modal}>
          <header>
            <Button priority="xhigh" size="small" onClick={handleInitializeDraft}>
              New List
            </Button>
            <h4>Manage tags</h4>
          </header>
          <div className={styles.scrollable}>{contents}</div>
        </div>
      </Modal>
    );
  }

  return contents;
};

export default TagsEditor;
